import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import LoginLanding from './pages/LoginLanding';
import Register from './pages/Register';
import CheckEmail from './pages/CheckEmail';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Workflows from './pages/Workflows';
import TaskBoard from './pages/TaskBoard';
import TaskDetail from './pages/TaskDetail';
import RoleManagement from './pages/RoleManagement';
import ApprovalRequests from './pages/ApprovalRequests';
import Files from './pages/Files';
import ProfileSettings from './pages/ProfileSettings';
import TagManagement from './pages/TagManagement';
import Reports from './pages/Reports';
import VerifyEmail from './pages/VerifyEmail';
import NotificationSettings from './pages/NotificationSettings';
import NotificationHistory from './pages/NotificationHistory';
import AuditLog from './pages/AuditLog';
import SecurityScan from './pages/SecurityScan';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import BillingSettings from './pages/BillingSettings';
import WorkspaceMembers from './pages/WorkspaceMembers';
import AcceptInvite from './pages/AcceptInvite';
import BillingCallback from './pages/BillingCallback';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import GlobalUsers from './pages/super-admin/GlobalUsers';
import GlobalWorkspaces from './pages/super-admin/GlobalWorkspaces';
import SuperAdminAuditLog from './pages/super-admin/SuperAdminAuditLog';
import EmailCampaigns from './pages/super-admin/EmailCampaigns';
import EmailCampaignHistory from './pages/super-admin/EmailCampaignHistory';
import Layout from './components/Layout';
import UpdatePrompt from './components/UpdatePrompt';
import OrientationLock from './components/OrientationLock';
import UpgradeModal from './components/UpgradeModal';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './design/ThemeContext';
import { WorkspaceProvider, getActiveWorkspaceIdFromStorage } from './contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = false;

// Auth token yönetimi
const getAuthToken = () => localStorage.getItem('session_token');
const setAuthToken = (token) => {
  if (token) localStorage.setItem('session_token', token);
  else localStorage.removeItem('session_token');
};

// Pub/sub for the global upgrade modal — billing 402 responses surface here.
const upgradeListeners = new Set();
export const onQuotaExceeded = (cb) => {
  upgradeListeners.add(cb);
  return () => upgradeListeners.delete(cb);
};

// ⚡ PERFORMANCE: Axios interceptor ile tüm request'leri log'la ve token ekle
axios.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: performance.now() };
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Stamp the active workspace so backend quota / membership checks resolve
    // against the correct billing entity.
    const workspaceId = getActiveWorkspaceIdFromStorage();
    if (workspaceId && !config.headers['X-Workspace-Id']) {
      config.headers['X-Workspace-Id'] = workspaceId;
    }
    console.log(`📤 [API REQUEST] ${config.method.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    const duration = performance.now() - response.config.metadata.startTime;
    const size = JSON.stringify(response.data).length;
    console.log(`📥 [API RESPONSE] ${response.config.method.toUpperCase()} ${response.config.url} - ${duration.toFixed(0)}ms (${(size/1024).toFixed(1)}KB)`);
    return response;
  },
  (error) => {
    if (error.config && error.config.metadata) {
      const duration = performance.now() - error.config.metadata.startTime;
      console.error(`❌ [API ERROR] ${error.config.method.toUpperCase()} ${error.config.url} - ${duration.toFixed(0)}ms - ${error.message}`);
    }
    // Surface quota errors to the global upgrade modal listeners.
    const detail = error.response?.data?.detail;
    if (error.response?.status === 402 && detail?.error === 'quota_exceeded') {
      upgradeListeners.forEach(cb => {
        try { cb(detail); } catch (_) { /* noop */ }
      });
    }
    return Promise.reject(error);
  }
);

export const AuthContext = React.createContext();

export const useAuth = () => React.useContext(AuthContext);

// Giriş yapılmamışsa hedef URL'i kaydedip login'e yönlendir
function SaveRedirectAndLogin() {
  const { taskId } = useParams();
  // Login sonrası TaskBoard'da drawer açılsın
  localStorage.setItem('redirectAfterLogin', `/tasks?openTask=${taskId}`);
  return <Navigate to="/login" />;
}

// /shared/task/:taskId → /tasks?openTask=:taskId (mevcut drawer mekanizması)
function SharedTaskRedirect() {
  const { taskId } = useParams();
  return <Navigate to={`/tasks?openTask=${taskId}`} replace />;
}

// Süper admin sayfaları için guard. Süper admin değilse /dashboard'a yollar.
function RequireSuperAdmin({ user, children }) {
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return children;
}

// Henüz workspace'i olmayan giriş yapmış kullanıcıyı onboarding'e yönlendirir.
// Süper admin'ler tüm workspace'leri görebildikleri için muaftır.
function OnboardingGate({ user, children }) {
  if (!user) return children;
  if (user.role === 'super_admin') return children;
  const list = user.workspaceIds || [];
  if (list.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function GatedLayout({ children }) {
  const { user } = useAuth();
  return (
    <OnboardingGate user={user}>
      <Layout>{children}</Layout>
    </OnboardingGate>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    checkAuth();
    setupServiceWorkerUpdateListener();
    
    // PRODUCTION FIX: Eski cache'leri temizle
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => {
          if (key !== 'onxflow-v3.2.0') {
            caches.delete(key);
          }
        });
      });
    }
  }, []);

  const setupServiceWorkerUpdateListener = () => {
    if ('serviceWorker' in navigator) {
      let refreshing = false;

      // controllerchange: yeni SW aktif oldu, sayfayı bir kez yenile
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      navigator.serviceWorker.ready.then((registration) => {
        // Bekleyen SW varsa hemen aktif et
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Yeni SW yüklendiğinde
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Yeni versiyon yüklendi - zorunlu güncelleme prompt göster
              setWaitingWorker(newWorker);
              setShowUpdatePrompt(true);

              // 5 saniye sonra otomatik güncelle
              setTimeout(() => {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }, 5000);
            }
          });
        });

        // Her 60 saniyede güncelleme kontrolü
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60000);
      });
    }
  };

  const checkAuth = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSuspended(false);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "Account suspended") {
        setSuspended(true);
      } else {
        setUser(null);
        setSuspended(false);
        setAuthToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setAuthToken(null);
    setUser(null);
    setSuspended(false);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg)', color: 'var(--text)' }}
      >
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-full animate-spin mx-auto mb-4"
            style={{ border: '4px solid var(--border-strong)', borderTopColor: 'transparent' }}
          ></div>
          <p className="ox-text-muted">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (suspended) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg)' }}
      >
        <div className="max-w-md w-full mx-4">
          <div
            className="rounded-2xl shadow-xl p-8 text-center"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'var(--warn-weak)' }}
            >
              <svg className="w-10 h-10" style={{ color: 'var(--warn)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--text)' }}>Hesap Askıda</h1>
            <p className="mb-6 ox-text-muted">
              Hesabınız yönetici tarafından askıya alınmıştır. Daha fazla bilgi için sistem yöneticisiyle iletişime geçin.
            </p>
            <button
              onClick={logout}
              className="w-full text-white py-3 px-6 rounded-lg transition-all"
              style={{ background: 'var(--accent)' }}
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>
      <ThemeProvider>
      <WorkspaceProvider>
      <BrowserRouter>
        <OrientationLock>
          <GlobalUpgradeModal />
          <Routes>
          {/* Public marketing landing — index sayfasi */}
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />

          {/* Login sayfasi (eskiden /'da idi) */}
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginLanding />} />

          {/* Geriye donuk uyumluluk: /landing eski URL'leri / 'a yonlendir */}
          <Route path="/landing" element={<Navigate to="/" replace />} />

          {/* Email Verification Route - Public */}
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Public auth flow routes */}
          <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route
            path="/onboarding"
            element={user ? <Onboarding /> : <Navigate to="/login" />}
          />

          {/* Billing & invite public routes */}
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/billing/callback" element={<BillingCallback />} />
          <Route path="/pricing" element={user ? <Pricing /> : <Navigate to="/login" />} />
          <Route path="/contact" element={<Contact />} />
          
          <Route
            path="/dashboard"
            element={user ? <GatedLayout><Dashboard /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/teams"
            element={user ? <GatedLayout><Teams /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/projects"
            element={user ? <GatedLayout><Projects /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/projects/:projectId"
            element={user ? <GatedLayout><ProjectDetail /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/workflows"
            element={user ? <GatedLayout><Workflows /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/tasks"
            element={user ? <GatedLayout><TaskBoard /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/tasks/:taskId"
            element={user ? <GatedLayout><TaskDetail /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route path="/users" element={<Navigate to="/settings/members" replace />} />
          <Route
            path="/approvals"
            element={user ? <GatedLayout><ApprovalRequests /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/roles"
            element={user ? <GatedLayout><RoleManagement /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/files"
            element={user ? <GatedLayout><Files /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/profile"
            element={user ? <Layout><ProfileSettings /></Layout> : <Navigate to="/login" />}
          />
          <Route
            path="/tags"
            element={user ? <GatedLayout><TagManagement /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/reports"
            element={user ? <GatedLayout><Reports /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/audit-log"
            element={user ? <GatedLayout><AuditLog /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/security"
            element={user ? <GatedLayout><SecurityScan user={user} /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/notifications/settings"
            element={user ? <GatedLayout><NotificationSettings /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/notifications/history"
            element={user ? <GatedLayout><NotificationHistory /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/settings/billing"
            element={user ? <GatedLayout><BillingSettings /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/settings/members"
            element={user ? <GatedLayout><WorkspaceMembers /></GatedLayout> : <Navigate to="/login" />}
          />
          <Route
            path="/shared/task/:taskId"
            element={user ? <SharedTaskRedirect /> : <SaveRedirectAndLogin />}
          />

          {/* Süper Admin alanı - tüm endpointler süper admin guard ile sarmalı */}
          <Route
            path="/super-admin"
            element={<RequireSuperAdmin user={user}><Layout><SuperAdminDashboard /></Layout></RequireSuperAdmin>}
          />
          <Route
            path="/super-admin/users"
            element={<RequireSuperAdmin user={user}><Layout><GlobalUsers /></Layout></RequireSuperAdmin>}
          />
          <Route
            path="/super-admin/workspaces"
            element={<RequireSuperAdmin user={user}><Layout><GlobalWorkspaces /></Layout></RequireSuperAdmin>}
          />
          <Route
            path="/super-admin/campaigns"
            element={<RequireSuperAdmin user={user}><Layout><EmailCampaigns /></Layout></RequireSuperAdmin>}
          />
          <Route
            path="/super-admin/campaigns/history"
            element={<RequireSuperAdmin user={user}><Layout><EmailCampaignHistory /></Layout></RequireSuperAdmin>}
          />
          <Route
            path="/super-admin/audit"
            element={<RequireSuperAdmin user={user}><Layout><SuperAdminAuditLog /></Layout></RequireSuperAdmin>}
          />
        </Routes>
        <Toaster position="top-right" />
        
        {/* Update Prompt - Yeni versiyon mevcut */}
        {showUpdatePrompt && (
          <UpdatePrompt
            onUpdate={() => {
              setShowUpdatePrompt(false);
              
              if (waitingWorker) {
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });
              }
              
              setTimeout(() => {
                if ('caches' in window) {
                  caches.keys().then(keys => {
                    Promise.all(keys.map(key => caches.delete(key)))
                    .then(() => window.location.reload(true));
                  });
                } else {
                  window.location.reload(true);
                }
              }, 100);
            }}
          />
        )}
        </OrientationLock>
      </BrowserRouter>
      </WorkspaceProvider>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

function GlobalUpgradeModal() {
  const [info, setInfo] = useState(null);
  useEffect(() => onQuotaExceeded((d) => setInfo(d)), []);
  return <UpgradeModal open={!!info} info={info} onClose={() => setInfo(null)} />;
}

export default App;
